"use client"

import { useEffect, useRef, useState } from "react"
import { Link2, Loader2, MessageSquare, Plus, Search, Settings, Trash2, X } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "react-oidc-context"
import { useRole } from "@/hooks/useRole"
import {
  SessionSummary,
  SessionSearchResult,
  searchSessions,
} from "@/services/sessionService"
import { ProjectSummary } from "@/services/projectService"
import NetSuiteSettingsDialog from "./NetSuiteSettingsDialog"
import { Input } from "@/components/ui/input"
import {
  Sidebar,
  SidebarContent,
  useSidebar,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuAction,
  SidebarFooter,
} from "@/components/ui/sidebar"
import { Button } from "@/components/ui/button"
import { ProjectSelector } from "./ProjectSelector"

type ChatSidebarProps = {
  sessions: SessionSummary[]
  currentSessionId?: string | null
  onSessionSelect: (sessionId: string) => void
  onNewChat: () => void
  onDeleteSession: (sessionId: string) => void
  hasMore: boolean
  isLoadingMore: boolean
  onLoadMore: () => void
  projects: ProjectSummary[]
  currentProjectId: string | null
  onProjectSelect: (projectId: string | null) => void
  onNewProject: () => void
  onProjectSettings: (projectId: string) => void
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return "今日"
  if (diffDays === 1) return "昨日"
  if (diffDays < 7) return `${diffDays}日前`
  return date.toLocaleDateString("ja-JP", { month: "short", day: "numeric" })
}

export function ChatSidebar({
  sessions,
  currentSessionId,
  onSessionSelect,
  onNewChat,
  onDeleteSession,
  hasMore,
  isLoadingMore,
  onLoadMore,
  projects,
  currentProjectId,
  onProjectSelect,
  onNewProject,
  onProjectSettings,
}: ChatSidebarProps) {
  const { isAdmin } = useRole()
  const navigate = useNavigate()
  const [netsuiteDialogOpen, setNetsuiteDialogOpen] = useState(false)
  // ナレッジ管理は管理メニュー（/admin/knowledge）へ移設（2026-06-14）。チャット左の独立リンクは廃止。

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!hasMore || isLoadingMore) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore()
        }
      },
      { threshold: 0.1 }
    )
    const sentinel = sentinelRef.current
    if (sentinel) observer.observe(sentinel)
    return () => {
      if (sentinel) observer.unobserve(sentinel)
    }
  }, [hasMore, isLoadingMore, onLoadMore])

  // モバイル: サイドバーはシート（ドロワー）になるが、会話やプロジェクトを選んでも
  // 開いたままで毎回手動で閉じる必要があった。選択操作後は自動で閉じる。
  const { isMobile, setOpenMobile } = useSidebar()
  const closeOnMobile = () => {
    if (isMobile) setOpenMobile(false)
  }

  // 履歴検索（2026-07-02 P3）: Enter で確定検索（タイトル＋本文・サーバ側走査）。
  // 結果表示中は通常の一覧を検索結果に差し替え、✕ で戻す。
  const auth = useAuth()
  const [query, setQuery] = useState("")
  const [searchResults, setSearchResults] = useState<SessionSearchResult[] | null>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [searchNote, setSearchNote] = useState("")

  const runSearch = async () => {
    const q = query.trim()
    const idToken = auth.user?.id_token
    if (q.length < 2 || !idToken) return
    setIsSearching(true)
    try {
      const res = await searchSessions(idToken, q)
      setSearchResults(res.results)
      setSearchNote(
        res.contentScanLimited
          ? "本文検索は直近の会話まで（タイトルは全件が対象です）"
          : ""
      )
    } catch (e) {
      setSearchResults([])
      setSearchNote(e instanceof Error ? e.message : "検索に失敗しました")
    } finally {
      setIsSearching(false)
    }
  }

  const clearSearch = () => {
    setQuery("")
    setSearchResults(null)
    setSearchNote("")
  }

  return (
    <Sidebar>
      <SidebarHeader className="p-4 space-y-2">
        <ProjectSelector
          projects={projects}
          currentProjectId={currentProjectId}
          onProjectSelect={(id) => {
            closeOnMobile()
            onProjectSelect(id)
          }}
          onNewProject={() => {
            closeOnMobile()
            onNewProject()
          }}
          onProjectSettings={(id) => {
            closeOnMobile()
            onProjectSettings(id)
          }}
        />
        <Button onClick={() => { closeOnMobile(); onNewChat() }} className="w-full justify-start gap-2">
          <Plus className="h-4 w-4" />
          新しいチャット
        </Button>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              if (e.target.value === "") clearSearch()
            }}
            onKeyDown={(e) => {
              // IME 変換確定の Enter では発火させない（日本語入力）
              if (e.key === "Enter" && !e.nativeEvent.isComposing) runSearch()
              if (e.key === "Escape") clearSearch()
            }}
            placeholder="履歴を検索（Enter で実行）"
            className="pl-8 pr-8"
            aria-label="チャット履歴を検索"
          />
          {(query || searchResults !== null) && (
            <button
              onClick={clearSearch}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              title="検索をクリア"
              aria-label="検索をクリア"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </SidebarHeader>
      <SidebarContent>
        {searchResults !== null ? (
          <SidebarGroup>
            <SidebarGroupLabel>
              {isSearching ? "検索中…" : `検索結果（${searchResults.length}件）`}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              {searchNote && (
                <div className="px-4 pb-2 text-xs text-muted-foreground">{searchNote}</div>
              )}
              <SidebarMenu>
                {isSearching ? (
                  <div className="flex justify-center py-6">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                    一致する会話が見つかりませんでした
                  </div>
                ) : (
                  searchResults.map((r) => (
                    <SidebarMenuItem key={r.sessionId}>
                      <SidebarMenuButton
                        onClick={() => { closeOnMobile(); onSessionSelect(r.sessionId) }}
                        isActive={currentSessionId === r.sessionId}
                        className="w-full justify-start gap-2 h-auto py-2"
                      >
                        <MessageSquare className="h-4 w-4 shrink-0" />
                        <div className="flex flex-col min-w-0">
                          <span className="truncate">{r.title}</span>
                          {r.snippet && (
                            <span className="text-xs text-muted-foreground line-clamp-2">
                              {r.snippet}
                            </span>
                          )}
                          <span className="text-xs text-muted-foreground">
                            {formatDate(r.updatedAt)}
                          </span>
                        </div>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
        <SidebarGroup>
          <SidebarGroupLabel>最近のチャット</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {sessions.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  会話はまだありません
                </div>
              ) : (
                <>
                  {sessions.map((session) => (
                    <SidebarMenuItem key={session.sessionId}>
                      <SidebarMenuButton
                        onClick={() => { closeOnMobile(); onSessionSelect(session.sessionId) }}
                        isActive={currentSessionId === session.sessionId}
                        className="w-full justify-start gap-2"
                      >
                        <MessageSquare className="h-4 w-4 shrink-0" />
                        <div className="flex flex-col min-w-0">
                          <span className="truncate">{session.title}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(session.updatedAt)}
                          </span>
                        </div>
                      </SidebarMenuButton>
                      <SidebarMenuAction
                        showOnHover
                        onClick={(e) => {
                          e.stopPropagation()
                          onDeleteSession(session.sessionId)
                        }}
                        title="会話を削除"
                      >
                        <Trash2 className="h-4 w-4" />
                      </SidebarMenuAction>
                    </SidebarMenuItem>
                  ))}
                  {hasMore && (
                    <div ref={sentinelRef} className="flex justify-center py-2">
                      {isLoadingMore && (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      )}
                    </div>
                  )}
                </>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarFooter className="p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={() => setNetsuiteDialogOpen(true)}>
              <Link2 className="h-4 w-4" />
              <span>NetSuite連携設定</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {/* 管理メニューの入口は認証済み全員に開く（2026-06-14）。中身はロール別:
              member/viewer は AIアシスタント設定だけ（/admin の index が /admin/agent へ着地）、
              admin/owner は管理一式。ラベルは admin に「管理」、それ以外に「設定」。 */}
          <SidebarMenuItem>
            <SidebarMenuButton onClick={() => navigate("/admin")}>
              <Settings className="h-4 w-4" />
              <span>{isAdmin ? "管理" : "設定"}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <NetSuiteSettingsDialog
        open={netsuiteDialogOpen}
        onOpenChange={setNetsuiteDialogOpen}
      />
    </Sidebar>
  )
}
